"use client";

import logger from "@/utils/logger";
// hooks/useInactivityTimeout.ts

import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useAuth } from "@/context/auth-context";
import { useGlobalLogout } from "./useGlobalLogout";
import { AUTH_STATE_KEY } from "./useGlobalLogout";
import {
    DEFAULT_INACTIVITY_DURATIONS,
    resolveInactivityDurations,
} from "@/config/inactivity";

interface InactivityConfig {
    timeoutDuration?: number; // in milliseconds
    warningDuration?: number; // show warning before logout (in milliseconds)
    enableWarning?: boolean;
    enableInactivityDetection?: boolean;
    sessionActive?: boolean;
}

const DEFAULT_CONFIG: Required<Omit<InactivityConfig, "sessionActive">> = {
    timeoutDuration: DEFAULT_INACTIVITY_DURATIONS.timeoutDuration,
    warningDuration: DEFAULT_INACTIVITY_DURATIONS.warningDuration,
    enableWarning: true,
    enableInactivityDetection: true,
};

// Events that indicate user activity
const ACTIVITY_EVENTS = [
    "mousedown",
    "mousemove",
    "keypress",
    "scroll",
    "touchstart",
    "click",
    "keydown",
    "wheel",
] as const;

// Key for storing last activity timestamp in localStorage for cross-tab communication
const LAST_ACTIVITY_KEY = "app_last_activity";
// Key for broadcasting explicit session extension across tabs
const SESSION_EXTENDED_KEY = "app_session_extended";

export interface InactivityState {
    isWarningShown: boolean;
    timeRemaining: number;
    isTimedOut: boolean;
    lastActivity: Date;
}

type TimerScheduleResult = "scheduled" | "warning_shown" | "timed_out";

export const useInactivityTimeout = (config: InactivityConfig = {}) => {
    const { isAuthenticated } = useAuth();
    const { triggerGlobalLogout } = useGlobalLogout();
    const resolvedDurations = useMemo(
        () =>
            resolveInactivityDurations(
                typeof window === "undefined" ? null : window.localStorage
            ),
        []
    );

    const finalConfig = { ...DEFAULT_CONFIG, ...resolvedDurations, ...config };
    const isSessionActive = finalConfig.sessionActive ?? isAuthenticated;

    const [state, setState] = useState<InactivityState>({
        isWarningShown: false,
        timeRemaining: finalConfig.timeoutDuration,
        isTimedOut: false,
        lastActivity: new Date(),
    });

    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
    const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
    const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
    const countdownGenerationRef = useRef(0);
    const lastActivityRef = useRef<Date>(new Date());
    const lastHandledActivityMsRef = useRef<number>(0);

    // Clear all timers
    const clearAllTimers = useCallback(() => {
        // Invalidate any in-flight countdown callback before clearing timers.
        countdownGenerationRef.current += 1;

        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    }, []);

    // Start countdown timer for warning
    const startCountdown = useCallback(() => {
        const startTime = Date.now();
        const generation = ++countdownGenerationRef.current;
        logger.info(
            "🕒 Starting countdown timer, warning duration:",
            finalConfig.warningDuration / 1000,
            "seconds"
        );
        logger.info("🕒 Start time:", new Date(startTime).toLocaleTimeString());

        // Clear any existing countdown timer first
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
        }

        countdownTimerRef.current = setInterval(() => {
            if (generation !== countdownGenerationRef.current) {
                if (countdownTimerRef.current) {
                    clearInterval(countdownTimerRef.current);
                    countdownTimerRef.current = null;
                }
                return;
            }

            const elapsed = Date.now() - startTime;
            const remaining = finalConfig.warningDuration - elapsed;

            logger.info(
                "⏰ Countdown update - elapsed:",
                Math.floor(elapsed / 1000),
                "s, remaining:",
                Math.floor(remaining / 1000),
                "s"
            );

            if (remaining <= 0) {
                if (generation !== countdownGenerationRef.current) {
                    return;
                }
                logger.info(
                    "🚨 Countdown finished - clearing timer and logging out"
                );
                clearInterval(countdownTimerRef.current!);
                countdownTimerRef.current = null;

                setState((prev) => ({ ...prev, isTimedOut: true }));
                triggerGlobalLogout();
            } else {
                logger.info(
                    "🔄 Updating state with remaining time:",
                    remaining
                );
                setState((prev) => ({ ...prev, timeRemaining: remaining }));
            }
        }, 100); // Update every 100ms for smooth progress bar
    }, [finalConfig.warningDuration, triggerGlobalLogout]);

    // Show warning before logout
    const showInactivityWarning = useCallback(() => {
        logger.info("⚠️ Showing inactivity warning...", {
            warningDuration: finalConfig.warningDuration,
            warningDurationSeconds: finalConfig.warningDuration / 1000,
        });

        // Set the warning state immediately
        setState((prev) => ({
            ...prev,
            isWarningShown: true,
            timeRemaining: finalConfig.warningDuration,
        }));

        // Start the countdown timer
        startCountdown();
    }, [finalConfig.warningDuration, startCountdown]);

    const scheduleInactivityTimers = useCallback(
        (elapsedMs = 0): TimerScheduleResult => {
            const elapsed = Math.max(0, elapsedMs);

            if (elapsed >= finalConfig.timeoutDuration) {
                logger.info(
                    "🚨 Inactivity timeout already reached, logging out user..."
                );
                setState((prev) => ({ ...prev, isTimedOut: true }));
                triggerGlobalLogout();
                return "timed_out";
            }

            if (finalConfig.enableWarning) {
                const warningTime =
                    finalConfig.timeoutDuration - finalConfig.warningDuration;
                const remainingWarningTime = warningTime - elapsed;

                if (remainingWarningTime <= 0) {
                    showInactivityWarning();
                    return "warning_shown";
                }

                warningTimerRef.current = setTimeout(() => {
                    showInactivityWarning();
                }, remainingWarningTime);
                return "scheduled";
            }

            const remainingTime = finalConfig.timeoutDuration - elapsed;
            inactivityTimerRef.current = setTimeout(() => {
                logger.info(
                    "🚨 Inactivity timeout reached (no warning), logging out user..."
                );
                setState((prev) => ({ ...prev, isTimedOut: true }));
                triggerGlobalLogout();
            }, remainingTime);
            return "scheduled";
        },
        [
            finalConfig.enableWarning,
            finalConfig.timeoutDuration,
            finalConfig.warningDuration,
            showInactivityWarning,
            triggerGlobalLogout,
        ]
    );

    // Handle user activity
    const handleActivity = useCallback(() => {
        if (!finalConfig.enableInactivityDetection || !isSessionActive) {
            return;
        }

        const now = new Date();
        lastActivityRef.current = now;

        // Store activity timestamp in localStorage for cross-tab communication
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, now.getTime().toString());
        } catch (error) {
            console.warn(
                "Failed to update activity timestamp in localStorage:",
                error
            );
        }

        // Use functional state update to check current warning state
        setState((prev) => {
            // If warning is already shown, don't reset it - user must click "Stay Logged In"
            if (prev.isWarningShown) {
                logger.info(
                    "⚠️ Activity detected but warning is shown - user must click 'Stay Logged In'"
                );
                return prev; // Return unchanged state
            }

            // Reset all timers and warning state
            clearAllTimers();

            // Start new inactivity timer
            scheduleInactivityTimers();

            // Return new state
            return {
                ...prev,
                isWarningShown: false,
                timeRemaining: finalConfig.timeoutDuration,
                isTimedOut: false,
                lastActivity: now,
            };
        });
    }, [
        finalConfig.enableInactivityDetection,
        finalConfig.timeoutDuration,
        isSessionActive,
        clearAllTimers,
        scheduleInactivityTimers,
    ]);

    const handleActivityEvent = useCallback(
        (event: Event) => {
            // Ignore synthetic/non-user events to avoid timer resets caused by
            // programmatic UI updates.
            if (!event.isTrusted) {
                return;
            }

            const noisyEventTypes = new Set(["mousemove", "scroll", "wheel"]);
            if (noisyEventTypes.has(event.type)) {
                const now = Date.now();
                if (now - lastHandledActivityMsRef.current < 1000) {
                    return;
                }
                lastHandledActivityMsRef.current = now;
            }

            handleActivity();
        },
        [handleActivity]
    );

    // Handle activity from other tabs
    const handleCrossTabActivity = useCallback(() => {
        if (!finalConfig.enableInactivityDetection || !isSessionActive) {
            return;
        }

        try {
            const storedTimestamp = localStorage.getItem(LAST_ACTIVITY_KEY);
            if (!storedTimestamp) return;

            const activityTime = new Date(Number.parseInt(storedTimestamp, 10));
            const currentLastActivity = lastActivityRef.current;

            // Only process if this activity is newer than our current last activity
            if (activityTime > currentLastActivity) {
                logger.info(
                    "🔄 Activity detected from another tab, resetting timers"
                );

                lastActivityRef.current = activityTime;

                // Use functional state update to check current warning state
                setState((prev) => {
                    // If warning is already shown, don't reset it - user must click "Stay Logged In"
                    if (prev.isWarningShown) {
                        logger.info(
                            "⚠️ Cross-tab activity detected but warning is shown - user must click 'Stay Logged In'"
                        );
                        return prev; // Return unchanged state
                    }

                    // Reset all timers and warning state
                    clearAllTimers();

                    const elapsed = Math.max(
                        0,
                        Date.now() - activityTime.getTime()
                    );
                    const timerResult = scheduleInactivityTimers(elapsed);
                    const remainingTime = Math.max(
                        0,
                        finalConfig.timeoutDuration - elapsed
                    );
                    const isTimedOut = timerResult === "timed_out";
                    const isWarningShown = timerResult === "warning_shown";
                    const timeRemaining = isWarningShown
                        ? finalConfig.warningDuration
                        : remainingTime;

                    // Return new state
                    return {
                        ...prev,
                        isWarningShown,
                        timeRemaining,
                        isTimedOut,
                        lastActivity: activityTime,
                    };
                });
            }
        } catch (error) {
            console.warn("Failed to process cross-tab activity:", error);
        }
    }, [
        finalConfig.enableInactivityDetection,
        finalConfig.timeoutDuration,
        finalConfig.warningDuration,
        isSessionActive,
        clearAllTimers,
        scheduleInactivityTimers,
    ]);

    // Extend session (called when user responds to warning)
    const extendSession = useCallback(() => {
        logger.info("✅ Session extended by user action");

        const now = new Date();
        lastActivityRef.current = now;

        // Store activity timestamp in localStorage for cross-tab communication
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, now.getTime().toString());
            localStorage.setItem(
                SESSION_EXTENDED_KEY,
                JSON.stringify({
                    timestamp: now.getTime(),
                    tabId: Math.random().toString(36).substr(2, 9),
                })
            );
            // Update auth state to prevent focus/visibility handlers from triggering logout
            localStorage.setItem(
                AUTH_STATE_KEY,
                JSON.stringify({
                    timestamp: now.getTime(),
                    authenticated: true,
                })
            );
        } catch (error) {
            console.warn(
                "Failed to update activity timestamp in localStorage:",
                error
            );
        }

        // Reset all timers and warning state
        clearAllTimers();
        setState((prev) => ({
            ...prev,
            isWarningShown: false,
            timeRemaining: finalConfig.timeoutDuration,
            isTimedOut: false,
            lastActivity: now,
        }));

        // Start new inactivity timer
        scheduleInactivityTimers();
    }, [finalConfig.timeoutDuration, clearAllTimers, scheduleInactivityTimers]);

    // Manually trigger logout
    const forceLogout = useCallback(() => {
        logger.info(
            "🚪 User manually triggered logout from inactivity warning"
        );
        clearAllTimers();
        setState((prev) => ({ ...prev, isTimedOut: true }));
        triggerGlobalLogout();
    }, [clearAllTimers, triggerGlobalLogout]);

    // Set up activity listeners
    useEffect(() => {
        if (!finalConfig.enableInactivityDetection || !isSessionActive) {
            clearAllTimers();
            return;
        }

        logger.info("🔍 Setting up inactivity detection...", {
            timeoutDuration: finalConfig.timeoutDuration / 60000 + " minutes",
            warningDuration: finalConfig.warningDuration / 60000 + " minutes",
            enableWarning: finalConfig.enableWarning,
        });

        // Add event listeners for activity detection
        ACTIVITY_EVENTS.forEach((event) => {
            document.addEventListener(event, handleActivityEvent, {
                passive: true,
                capture: true,
            });
        });

        // Initialize timer with current or stored activity
        const initializeActivity = () => {
            try {
                const storedTimestamp = localStorage.getItem(LAST_ACTIVITY_KEY);
                if (storedTimestamp) {
                    const storedActivityMs = Number.parseInt(
                        storedTimestamp,
                        10
                    );
                    if (Number.isFinite(storedActivityMs)) {
                        const storedActivity = new Date(storedActivityMs);
                        const elapsed = Date.now() - storedActivityMs;
                        // If stored activity is recent (within timeout window), reuse
                        // that timestamp and preserve remaining time.
                        if (elapsed < finalConfig.timeoutDuration) {
                            lastActivityRef.current = storedActivity;
                            logger.info(
                                "🔄 Using stored activity from cross-tab:",
                                storedActivity.toLocaleTimeString()
                            );

                            clearAllTimers();
                            const timerResult =
                                scheduleInactivityTimers(elapsed);
                            const remainingTime = Math.max(
                                0,
                                finalConfig.timeoutDuration - elapsed
                            );
                            const isWarningShown =
                                timerResult === "warning_shown";
                            const isTimedOut = timerResult === "timed_out";
                            const timeRemaining = isWarningShown
                                ? finalConfig.warningDuration
                                : remainingTime;

                            setState((prev) => ({
                                ...prev,
                                isWarningShown,
                                timeRemaining,
                                isTimedOut,
                                lastActivity: storedActivity,
                            }));
                            return;
                        }
                    }

                    // Stored activity is too old or invalid, start fresh.
                    handleActivity();
                    return;
                }

                // No stored activity, start fresh.
                handleActivity();
            } catch (error) {
                console.warn(
                    "Failed to initialize activity from localStorage:",
                    error
                );
                handleActivity();
            }
        };

        initializeActivity();

        // Cleanup
        return () => {
            ACTIVITY_EVENTS.forEach((event) => {
                document.removeEventListener(event, handleActivityEvent, {
                    capture: true,
                });
            });
            clearAllTimers();
        };
    }, [
        finalConfig.enableInactivityDetection,
        finalConfig.timeoutDuration,
        finalConfig.warningDuration,
        finalConfig.enableWarning,
        isSessionActive,
        handleActivity,
        handleActivityEvent,
        clearAllTimers,
        scheduleInactivityTimers,
    ]);

    // Listen for cross-tab activity via localStorage changes
    useEffect(() => {
        if (!finalConfig.enableInactivityDetection || !isSessionActive) {
            return;
        }

        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
                logger.info(
                    "🔔 Activity detected from another tab via storage event"
                );
                handleCrossTabActivity();
            }
            if (event.key === SESSION_EXTENDED_KEY && event.newValue) {
                logger.info(
                    "🔔 Session extension detected from another tab via storage event"
                );
                try {
                    const payload = JSON.parse(event.newValue);
                    if (payload?.timestamp) {
                        const activityTime = new Date(payload.timestamp);
                        lastActivityRef.current = activityTime;
                    }
                } catch (error) {
                    console.warn(
                        "Failed to parse session extension payload:",
                        error
                    );
                }

                clearAllTimers();
                setState((prev) => ({
                    ...prev,
                    isWarningShown: false,
                    timeRemaining: finalConfig.timeoutDuration,
                    isTimedOut: false,
                    lastActivity: lastActivityRef.current,
                }));

                scheduleInactivityTimers();
            }
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [
        finalConfig.enableInactivityDetection,
        isSessionActive,
        handleCrossTabActivity,
        clearAllTimers,
        finalConfig.enableWarning,
        finalConfig.timeoutDuration,
        finalConfig.warningDuration,
        scheduleInactivityTimers,
    ]);

    // Recalibrate timers when the tab becomes visible again.
    // Background tabs can throttle timers, so we recompute from real elapsed time.
    useEffect(() => {
        if (!finalConfig.enableInactivityDetection || !isSessionActive) {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState !== "visible") {
                return;
            }

            if (state.isWarningShown) {
                logger.info(
                    "👁️ Tab visible but warning already shown — skipping recalibration"
                );
                return;
            }

            logger.info(
                "👁️ Tab became visible, recalculating inactivity timer..."
            );

            try {
                const storedTimestamp = localStorage.getItem(LAST_ACTIVITY_KEY);
                if (!storedTimestamp) {
                    return;
                }

                const storedActivityMs = Number.parseInt(storedTimestamp, 10);
                if (!Number.isFinite(storedActivityMs)) {
                    return;
                }

                const elapsed = Date.now() - storedActivityMs;
                const storedActivity = new Date(storedActivityMs);
                lastActivityRef.current = storedActivity;

                clearAllTimers();
                const timerResult = scheduleInactivityTimers(elapsed);
                const remainingTime = Math.max(
                    0,
                    finalConfig.timeoutDuration - elapsed
                );
                const isWarningShown = timerResult === "warning_shown";
                const isTimedOut = timerResult === "timed_out";
                const timeRemaining = isWarningShown
                    ? finalConfig.warningDuration
                    : remainingTime;

                setState((prev) => ({
                    ...prev,
                    isWarningShown,
                    timeRemaining,
                    isTimedOut,
                    lastActivity: storedActivity,
                }));
            } catch (error) {
                console.warn("Failed to handle visibility change:", error);
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, [
        finalConfig.enableInactivityDetection,
        finalConfig.timeoutDuration,
        finalConfig.warningDuration,
        isSessionActive,
        state.isWarningShown,
        clearAllTimers,
        scheduleInactivityTimers,
    ]);

    // Clear timers when user logs out
    useEffect(() => {
        if (!isSessionActive) {
            clearAllTimers();
            // Clear the activity timestamp from localStorage when logging out
            try {
                localStorage.removeItem(LAST_ACTIVITY_KEY);
            } catch (error) {
                console.warn(
                    "Failed to clear activity timestamp from localStorage:",
                    error
                );
            }
            setState({
                isWarningShown: false,
                timeRemaining: finalConfig.timeoutDuration,
                isTimedOut: false,
                lastActivity: new Date(),
            });
        }
    }, [isSessionActive, clearAllTimers, finalConfig.timeoutDuration]);

    return {
        ...state,
        extendSession,
        forceLogout,
        config: finalConfig,
    };
};
